import { Injectable, OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpResponse, HttpErrorResponse } from '@angular/common/http';

import { Subscription, of, Observable } from 'rxjs';
import { map, catchError, take } from 'rxjs/operators';

import { LoggingMessageService } from './logging-message.service';
import { DataResult } from '../models/data-result.mode';
import { AuthService } from './auth.service';
import { environment } from 'src/environments/environment';
import { IndicatorResolverModel } from '../models/indicator-resolver.model';
import { NavigationItem } from '../models/navigation-item.model';
import { UserNavigationMappingService } from './user-navigation-mapping.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { SubscriptionChangeService } from './subscription-change.service';


@Injectable({
    providedIn: 'root'
})
export class IndicatorResolverService implements OnInit, OnDestroy {
    private _BASE_URL: string = environment.BusinessApi;
    private _LOCAL_NAVIGATION_API = environment.LocalJsonApi;
    private $subscriptions: Subscription = new Subscription();
    private _NO_DATA = { message: '' };

    constructor(private http: HttpClient, private authService: AuthService, private logger: LoggingMessageService, 
        private navigationMappingService: UserNavigationMappingService, 
        private storageService: SessionStorageService,
        private subscriptionService: SubscriptionChangeService) {
    }

    ngOnInit(): void {

    }

    public getGetIndicatorFullRoute(user: string, navItem: NavigationItem, navigationExtras: any, indid: number = null): Observable<DataResult> {
        if (environment.UseServerForIndicatorResolver) {
            return this.getGetIndicatorFullRouteFromBusinessApi(user, navItem, navigationExtras, indid);
        } else {
            return this.getGetIndicatorFullRouteFromLocalApi(user, navItem, navigationExtras, indid);
        }
    }

    
    private getGetIndicatorFullRouteFromBusinessApi(user: string, navItem: NavigationItem, navigationExtras: any, indid: number): Observable<DataResult> {
        const webmethod = 'IndicatorPath/IndicatorChildPaths';
        const params = navigationExtras.queryParams;

        const uri = `${this._BASE_URL}${webmethod}?indicatorId=${indid}&hospId=${params.hospid}&unitId=${params.unitid}`;
        const media = 'application/json';

        return this.http.get<any>(
            uri,
            {
                headers: { Accept: media },
                observe: 'response'
            }
        )
            .pipe(
                map(response => {
                    this.logger.debug('indicator-resolver.service', 'getGetIndicatorFullRouteFromBusinessApi', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, 'Received indicator resolver path', '');
                    let result: DataResult;
                    if (response instanceof HttpResponse) {
                        const respBody: any = response.body;
                        if (response.status === 200) {
                            const json = this.isPlainObject(respBody) ? respBody : JSON.parse(respBody);
                            const np = this.navigationMappingService.mapNavigationItem(json, navItem.ItemID);

                            // Set the correct paths on the Indicator child items before navigating
                            const nidocs = navItem.Items.find(ni => ni.PermItemID.includes(environment.IndicatorDocumentSuffix));
                            const docs = np.Items.find(n => n.PermItemID.includes(environment.IndicatorDocumentSuffix));
                            if (nidocs && docs) {
                                nidocs.Path = docs.Path;
                                nidocs.PermItemID = docs.PermItemID;
                            }
                            const nitut = navItem.Items.find(ni => ni.PermItemID.includes(environment.IndicatorTutorialSuffix));
                            const tutorial = np.Items.find(n => n.PermItemID.includes(environment.IndicatorTutorialSuffix));
                            if (nitut && tutorial) {
                                nitut.Path = tutorial.Path;
                                nitut.PermItemID = tutorial.PermItemID;
                            }
                            const niover = navItem.Items.find(ni => ni.PermItemID.includes(environment.IndicatorOverviewSuffix));
                            const overview = np.Items.find(n => n.PermItemID.includes(environment.IndicatorOverviewSuffix));
                            if (niover && overview) {
                                niover.Path  = overview.Path;
                                niover.PermItemID = overview.PermItemID;
                            }

                            result = new DataResult(response.ok, response.status, response.statusText, np);
                        } else if (response.status === 401) {
                            result = new DataResult(false, 401, response.statusText, null);
                        } else {
                            result = new DataResult(false, 400, response.statusText, null);
                        }
                    }
                    if (response instanceof HttpErrorResponse) {
                        result = new DataResult(false, 500, response.statusText, null);
                    }
                    return result;

                }),
                catchError(error => {
                    this.logger.warn('indicator-resolver.service', 'getGetIndicatorFullRouteFromBusinessApi', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,  'Error caught', '');
                    const message = this.getErrorMessage(error);
                    return this.getRouteError(`getGetIndicatorFullRouteFromBusinessApi failed`, message);
                }),
                take(1)
            );

        return of(null);
    }

    private getGetIndicatorFullRouteFromLocalApi(user: string, navItem: NavigationItem, navigationExtras: any, indid: number): Observable<DataResult> {
        const media = 'application/json';
        const uri = `${this._LOCAL_NAVIGATION_API}indicator-resolver-full.json`;
       
        const indicatorResolver = new IndicatorResolverModel();

        this.logger.debug('indicator-resolver.service', 'getGetIndicatorFullRouteFromLocalApi', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `calling url: ${uri}`, '');
        return this.http.get<DataResult>(uri,
            {
                observe: 'response'
            }
        )
            .pipe(
                map(response => {
                    let result: DataResult;
                    if (response instanceof HttpResponse) {
                        const respBody: any = response.body;
                        if (response.status === 200) {
                            const json = this.isPlainObject(respBody) ? respBody : JSON.parse(respBody);
                            const np = this.navigationMappingService.mapNavigationItem(json, navItem.ItemID);

                            // Set the correct paths on the Indicator child items before navigating
                            const nidocs = navItem.Items.find(ni => ni.PermItemID.includes(environment.IndicatorDocumentSuffix));
                            const docs = np.Items.find(n => n.PermItemID.includes(environment.IndicatorDocumentSuffix));
                            if (nidocs && docs) {
                                nidocs.Path = docs.Path;
                                nidocs.PermItemID = docs.PermItemID;
                            }
                            const nitut = navItem.Items.find(ni => ni.PermItemID.includes(environment.IndicatorTutorialSuffix));
                            const tutorial = np.Items.find(n => n.PermItemID.includes(environment.IndicatorTutorialSuffix));
                            if (nitut && tutorial) {
                                nitut.Path = tutorial.Path;
                                nitut.PermItemID = tutorial.PermItemID;
                            }
                            const niover = navItem.Items.find(ni => ni.PermItemID.includes(environment.IndicatorOverviewSuffix));
                            const overview = np.Items.find(n => n.PermItemID.includes(environment.IndicatorOverviewSuffix));
                            if (niover && overview) {
                                niover.Path  = overview.Path;
                                niover.PermItemID = overview.PermItemID;
                            }

                            result = new DataResult(response.ok, response.status, response.statusText, np);
                        } else if (response.status === 401) {
                            result = new DataResult(false, 401, response.statusText, null);
                        } else {
                            result = new DataResult(false, 400, response.statusText, null);
                        }
                    }
                    if (response instanceof HttpErrorResponse) {
                        result = new DataResult(false, 500, response.statusText, null);
                    }
                    return result;
                }),
                catchError(error => {
                    this.logger.warn('indicator-resolver.service', 'getGetIndicatorFullRouteFromLocalApi', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, 'Error caught', '');
                    const message = this.getErrorMessage(error);
                    return this.getRouteError(`getGetIndicatorFullRouteFromLocalApi failed`, message);
                }),
                take(1),
            );
    }

    private getErrorMessage(error: any): string {
        let message = error.message;
        if ((error.error) && (error.error.message)) {
            message = error.error.message;
        }
        return message;
    }

    private getRouteError(operation: string, error: any) {
        let result: any;
        const response = this._NO_DATA;
        this.logger.warn('indicator-resolver', 'getRouteError',  this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Operation:${operation}`, `Server Error:${error}`);
        response.message = error;
        const errorResult = new DataResult(false, 503, 'Service Unavailable error', response);
        result = errorResult;

        return of(result);
    }

    private isPlainObject(value: any): boolean {
        return typeof value === 'object' && value !== null && !Array.isArray(value) && Object.prototype.toString.call(value) === '[object Object]';
    }

    unsubscribe() {
        if (this.$subscriptions) {
            this.$subscriptions.unsubscribe();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe();
    }
}

